/**
 * `PlayerPort` con `hls.js` dietro.
 *
 * Una delle quattro attuazioni (06-RIPRODUZIONE §1). Le altre tre — AVPlay su Tizen, il
 * `<video>` di LG, ExoPlayer nell'involucro Android — staranno accanto a questa e
 * realizzeranno la stessa interfaccia.
 *
 * **Qui dentro non c'è nessuna regola di prodotto.** Non c'è l'attesa raccontata, non c'è
 * il piano di recupero, non c'è lo zapping: quelli stanno in `@player/domain` e valgono
 * identici sulle quattro attuazioni. Se una di quelle regole finisse qui, andrebbe
 * riscritta quattro volte.
 */

import Hls, { Events, ErrorTypes, ErrorDetails } from 'hls.js';
import type { AudioTrack, MediaKind, PlayerEvent, PlayerPort } from '@player/ports';
import {
  blankVideoRemedy,
  choosePipeline,
  videoIsBlank,
  type GatewayError,
  type PlaybackCapabilities,
  type PlaybackPipeline,
} from '@player/domain';

/**
 * Esiste un lettore nostro, che decodifica con WebCodecs invece di chiederlo al sistema?
 *
 * **No, non ancora**, ed è il motivo per cui questa costante è qui e vale `false`: la capacità che
 * conta quando si cerca un rimedio al nero non è «il browser ha `VideoDecoder`», è «abbiamo una
 * strada alternativa funzionante». Le due cose si confondono facilmente, e confonderle vorrebbe dire
 * dichiarare a chi guarda che si sta passando a un lettore che non esiste.
 *
 * Il giorno che esiste, questa riga diventa `true` ed è l'unico posto da cambiare. Nel frattempo la
 * presenza di WebCodecs **si riporta lo stesso** nel resoconto, perché è il dato che dice se quel
 * lavoro ha senso su quell'apparecchio.
 */
const WEBCODECS_PLAYER_EXISTS = false;

/** Per quanto si continua a guardare se arrivano fotogrammi, dopo che la riproduzione è partita. */
const VITALS_TICK_MS = 1_000;

/**
 * Cosa sa fare questo motore.
 *
 * ## Chi è l'autorità su «hls.js può girare qui»
 *
 * È **hls.js**, non noi. La prima stesura di questa funzione guardava i globali e pretendeva di
 * dedurlo: `mediaSource: supported && globalThis.MediaSource !== undefined`. Dove la libreria dice
 * di sì ma l'oggetto non è visibile da qui — e succede: con un polyfill, dentro un worker, o nelle
 * prove, dove hls.js è finto — la deduzione diceva «nessuna strada» su un apparecchio che ne aveva
 * una. Le prove dell'adattatore l'hanno colta subito, ed è esattamente il loro mestiere.
 *
 * Quindi `mediaSource` qui significa «hls.js ci si attacca», e `managedMediaSource` aggiunge
 * l'unica cosa che i globali sanno dire meglio: **a quale delle due** si attaccherà. La distinzione
 * conta perché su una sorgente gestita il sistema può sospendere la riproduzione quando vuole, e un
 * difetto che si vede solo lì non si riproduce da nessun'altra parte.
 */
function capabilitiesOf(video: HTMLVideoElement): PlaybackCapabilities {
  /*
    `window` e non `globalThis`.

    `globalThis` esiste da Chromium 71 e Tizen 5 monta il 63: su quei televisori la riga non dà un
    errore gestibile, **impedisce all'app di partire**. Non lo vede né il compilatore né le prove
    delle schermate — lo vede `tizen5.test.ts`, che ha preso anche questa.
  */
  const globale = window as unknown as Record<string, unknown>;
  const supported = Hls.isSupported();
  return {
    managedMediaSource: supported && globale.ManagedMediaSource !== undefined,
    mediaSource: supported,
    webCodecs: globale.VideoDecoder !== undefined,
    nativeHls: video.canPlayType('application/vnd.apple.mpegurl') !== '',
  };
}

export interface HlsPlayerOptions {
  readonly video: HTMLVideoElement;
  /** `performance.now()` iniettato: il banco di misura deve poter cronometrare il primo
   *  fotogramma con lo stesso orologio con cui cronometra il fuoco. */
  readonly elapsedMs: () => number;
}

/**
 * Una politica di tentativi con un fondo, per hls.js 1.7.
 *
 * I valori predefiniti di `hls.js` riprovano molto, e per una ragione buona: su una rete domestica
 * la maggior parte degli errori è passeggera. Ma su un canale **spento** non è passeggero niente, e
 * riprovare a oltranza diventa una raffica di richieste che il gateway conta — e per cui blocca
 * l'indirizzo.
 *
 * Tre tentativi con attesa raddoppiata: dopo circa sei secondi hls.js dichiara l'errore fatale,
 * l'adattatore lo riporta e smette. Chi vuole insistere è l'applicazione, che ha già la sua politica
 * (`retryPolicyFor`) e sa distinguere un avvio fallito da una caduta in visione — cosa che qui,
 * dentro il caricatore, non si può sapere.
 */
/**
 * Quando ha senso richiedere la stessa cosa, e quando è solo rumore.
 *
 * **Una risposta del server non si riprova.** `403`, `404`, `410`, `500`: il gateway ha risposto, e
 * ha detto no — ripetere la stessa richiesta darà la stessa risposta. Prima se ne facevano quattro
 * (il tentativo più tre), quindi ogni canale che non parte costava quattro richieste al gateway per
 * arrivare esattamente dove si era già al primo colpo: un messaggio d'errore.
 *
 * Restano i casi in cui riprovare cambia qualcosa davvero: **la risposta che non arriva** — timeout,
 * connessione caduta, `0` — dove il server non ha detto niente e il singhiozzo può passare.
 *
 * Le uniche eccezioni sono `408` e `429`, che *sono* il server che dice «riprova»: la prima è un
 * tempo scaduto, la seconda un limite di frequenza. Insistere sul resto è quel che riempie i
 * registri del gateway e consuma il tetto per niente.
 */
export function shouldRetryLoad(status: number | undefined, isTimeout: boolean): boolean {
  if (isTimeout) return true;
  // Nessuno stato: la risposta non è arrivata affatto.
  if (status === undefined || status === 0) return true;
  if (status === 408 || status === 429) return true;
  return status < 400;
}

function retryPolicy(maxNumRetry: number) {
  const retry = {
    maxNumRetry,
    retryDelayMs: 1_000,
    maxRetryDelayMs: 4_000,
    backoff: 'exponential' as const,
    shouldRetry: (
      _config: unknown,
      retryCount: number,
      isTimeout: boolean,
      loaderResponse?: { code?: number },
    ) => retryCount < maxNumRetry && shouldRetryLoad(loaderResponse?.code, isTimeout),
  };
  return {
    default: {
      maxTimeToFirstByteMs: 10_000,
      maxLoadTimeMs: 20_000,
      timeoutRetry: retry,
      errorRetry: retry,
    },
  };
}

export class HlsPlayer implements PlayerPort {
  private hls: Hls | null = null;
  private readonly listeners = new Set<(event: PlayerEvent) => void>();
  private firstFrameSeen = false;
  private playStartedAtMs = 0;
  /** L'orologio che controlla se arrivano fotogrammi. Vive quanto una riproduzione. */
  private vitalsTimer: ReturnType<typeof setInterval> | null = null;
  private playingSinceMs: number | null = null;
  private blankReported = false;
  private pipeline: PlaybackPipeline = 'none';
  private readonly video: HTMLVideoElement;

  constructor(private readonly options: HlsPlayerOptions) {
    this.video = options.video;
    this.video.addEventListener('playing', this.onPlaying);
    this.video.addEventListener('pause', this.onPause);
    this.video.addEventListener('waiting', this.onWaiting);
    this.video.addEventListener('ended', this.onEnded);
    this.video.addEventListener('timeupdate', this.onTimeUpdate);
    this.video.addEventListener('error', this.onMediaError);
  }

  play(url: string, kind: MediaKind = 'hls'): void {
    this.teardownHls();
    /*
      **Ogni avvio riparte da uno stato noto: audio acceso.**

      L'elemento `<video>` è **uno solo** e vive quanto la schermata: i lettori si succedono sopra di
      esso. Quindi `muted = true`, messo per rimediare a un rifiuto dell'avvio automatico, restava
      sull'elemento — e tutti i canali successivi erano muti, mentre l'applicazione credeva che
      l'audio fosse acceso perché lo stato lo teneva lei.

      Il sintomo è «l'audio non c'è più» dopo aver cambiato canale una volta, e non si risale alla
      causa: nel momento in cui lo si nota, il canale che aveva provocato il muto è lontano.
    */
    this.video.muted = false;
    this.firstFrameSeen = false;
    this.playStartedAtMs = this.options.elapsedMs();
    this.emit({ type: 'loading' });

    this.stopVitals();

    /*
      La strada la sceglie il **dominio**, non questa funzione.

      Prima era una catena di `if` qui dentro, e quella catena conteneva una convinzione sbagliata
      che nessuno poteva vedere: «su iPhone si usa per forza l'HLS nativo», vero fino a iOS 17.0.
      Spostata nel dominio si prova senza un browser, e la riga che dice «da iOS 17.1 si usa la
      sorgente gestita» è una prova che fallisce se qualcuno la cambia.
    */
    this.pipeline = choosePipeline(capabilitiesOf(this.video), kind === 'file' ? 'file' : 'hls');
    this.emit({ type: 'pipeline', pipeline: this.pipeline });

    if (kind === 'file') {
      /*
        **Un film non passa da hls.js.**

        Il gateway serve il contenuto on demand come file — un MP4 su un worker, consegnato a
        intervalli di byte — e non come manifesto: non ha sessione e non ne avrà mai. hls.js davanti a
        un MP4 cerca un manifesto, non lo trova, esaurisce i tentativi e dichiara un errore di rete:
        il film resta su «Avvio…» e poi fallisce, con un codice che parla di rete per un contenuto che
        era lì e stava arrivando.

        L'elemento `<video>` sa aprirlo da sé, ed è anche l'unico modo di avere lo scorrimento libero
        dentro il file — che è la ragione per cui la schermata del lettore on demand ha le frecce e
        quella della diretta no.
      */
      this.video.src = url;
    } else if (this.pipeline === 'hls-mms' || this.pipeline === 'hls-mse') {
      const hls = new Hls({
        // Bassi di proposito: su una Fire TV Stick la memoria è circa un gigabyte
        // condiviso, e un buffer generoso è memoria che il sistema poi si riprende
        // chiudendo l'app in sottofondo (07-PRESTAZIONI §2).
        maxBufferLength: 20,
        maxMaxBufferLength: 40,
        backBufferLength: 30,
        enableWorker: true,
        lowLatencyMode: false,
        /*
          **I tentativi di hls.js hanno un fondo.**

          I valori predefiniti riprovano a lungo e con attese che, su un canale che non tornerà, si
          traducono in una raffica di richieste che non finisce mai. Il 27-09-2026 è costato il ban
          dell'IP sul gateway — `delivery_rate_limit`, 3000 richieste in un quarto d'ora — una ventina
          di volte in una sera.

          Qui si dichiarano espliciti e bassi: tre tentativi sul manifesto, tre sui frammenti, con
          un'attesa che cresce. Quando finiscono, hls.js emette un errore **fatale**, che è
          esattamente il segnale che questo adattatore sa gestire — e che prima non arrivava mai,
          perché i non fatali venivano ignorati e riprovati all'infinito.
        */
        manifestLoadPolicy: retryPolicy(3),
        playlistLoadPolicy: retryPolicy(3),
        fragLoadPolicy: retryPolicy(3),
      });
      hls.on(Events.ERROR, (_event, data) => {
        /*
          Un errore **non fatale** non è un non-problema: è un tentativo che hls.js sta per rifare.

          Prima qui c'era `if (!data.fatal) return;` — cioè «non è affar nostro» — e con i tentativi
          predefiniti significava lasciare il lettore a chiedere segmenti che non arriveranno, in
          eterno e senza che niente sullo schermo lo dicesse. Ora i tentativi hanno un fondo (sopra) e
          quando lo toccano l'errore diventa fatale: si riporta e **si smette di chiedere**.
        */
        if (!data.fatal) return;

        /*
          Si stacca **prima** di riportare.

          Riportare e continuare è il peggio dei due mondi: l'app dichiara il guasto, esaurisce i suoi
          tentativi, apre l'elenco dei canali — e intanto questa istanza continua a scaricare per un
          canale che nessuno sta più guardando. Era il secondo pezzo del ciclo che ha fatto scattare
          il ban: nessuno distruggeva il lettore quando si smetteva di aspettarlo.
        */
        hls.stopLoad();
        hls.detachMedia();

        this.emit({
          type: 'error',
          // Lo **stato HTTP** entra nella traduzione: senza, un 503 «canale non attivo» e una rete
          // che singhiozza diventano lo stesso errore, e l'app fa aspettare quindici secondi per
          // poi dire «riprova fra poco» a chi doveva sceglierne un altro.
          error: this.translateHlsError(data.type, data.details, data.response?.code),
        });
      });
      hls.on(Events.LEVEL_SWITCHED, () => {
        const level = hls.levels[hls.currentLevel];
        this.emit({ type: 'qualityChanged', height: level ? level.height : null });
      });
      hls.loadSource(url);
      hls.attachMedia(this.video);
      this.hls = hls;
    } else if (this.pipeline === 'native-hls') {
      // Safari e alcune WebView hanno HLS nativo: si usa quello, non si finge che
      // `hls.js` sia l'unica strada.
      this.video.src = url;
    } else {
      /*
        Nessuna strada. È la macchina, non il canale: non si riprova (06-RIPRODUZIONE §6, ultima
        riga). Su un box con WebView vecchia è il guasto più probabile di tutti.

        `webcodecs` finisce **qui dentro** finché il lettore che decodifica da sé non esiste: la
        regola del dominio sa già che quella strada sarebbe percorribile, e dichiararla percorsa
        senza averla scritta sarebbe la cosa peggiore — una funzione annunciata e mai in vigore.
      */
      this.emit({
        type: 'error',
        error: {
          code: 'stream.decoder_unsupported',
          params: {},
          retryable: false,
          atMs: Date.now(),
        },
      });
      return;
    }
    /*
      L'avvio automatico rifiutato si **rimedia**, non si tace.

      Prima il rifiuto finiva in un `catch` vuoto con un commento che diceva «il chiamante lo sa
      perché non arriva `firstFrame`». Il chiamante non lo sapeva: vedeva un avvio che non finiva
      mai, aspettava quindici secondi, dichiarava `stream.start_timeout` e riapriva l'elenco — su un
      canale che stava benissimo. Sullo schermo restava il primo fotogramma decodificato, quindi
      sembrava perfino che stesse andando.

      Il rimedio è quello che i browser hanno sempre concesso: **riprovare in muto**. L'avvio
      automatico senza audio non è mai bloccato. L'audio si riprende con un gesto dell'utente, e
      `mutedByPolicy` è come l'interfaccia lo viene a sapere per poterglielo chiedere.

      Si prova **prima** con l'audio: partire sempre in muto sarebbe una regressione su un
      televisore, dove nessuno blocca niente e la persona si ritroverebbe senza audio per una
      restrizione che lì non esiste.
    */
    void this.video.play().catch(() => {
      this.video.muted = true;
      void this.video
        .play()
        .then(() => this.emit({ type: 'mutedByPolicy' }))
        .catch(() => {
          // Rifiutato anche in muto: qui non c'è più un rimedio, e tacere lascerebbe l'avvio
          // appeso fino al tempo massimo. Meglio dirlo subito e con il proprio nome.
          this.emit({
            type: 'error',
            error: {
              code: 'playback.autoplay_blocked',
              params: {},
              retryable: false,
              atMs: Date.now(),
            },
          });
        });
    });
  }

  setMuted(muted: boolean): void {
    this.video.muted = muted;
    // Togliendo il muto il flusso può essere stato messo in pausa dalla politica del browser: si
    // rilancia. Se rifiuta ancora si resta com'era, senza inventare un errore per un tasto premuto.
    if (!muted && this.video.paused) void this.video.play().catch(() => undefined);
  }

  seek(positionSec: number): void {
    this.video.currentTime = positionSec;
  }

  pause(): void {
    this.video.pause();
  }

  resume(): void {
    void this.video.play().catch(() => undefined);
  }

  stop(): void {
    this.video.pause();
    this.teardownHls();
    this.video.removeAttribute('src');
    this.video.load();
  }

  audioTracks(): ReadonlyArray<AudioTrack> {
    if (!this.hls) return [];
    return this.hls.audioTracks.map((track) => ({
      id: String(track.id),
      label: track.name,
      language: track.lang ?? null,
    }));
  }

  selectAudioTrack(id: string): void {
    if (!this.hls) return;
    const index = this.hls.audioTracks.findIndex((track) => String(track.id) === id);
    if (index >= 0) this.hls.audioTrack = index;
  }

  subscribe(listener: (event: PlayerEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  destroy(): void {
    this.stop();
    this.stopVitals();
    this.video.removeEventListener('playing', this.onPlaying);
    this.video.removeEventListener('pause', this.onPause);
    this.video.removeEventListener('waiting', this.onWaiting);
    this.video.removeEventListener('ended', this.onEnded);
    this.video.removeEventListener('timeupdate', this.onTimeUpdate);
    this.video.removeEventListener('error', this.onMediaError);
    this.listeners.clear();
  }

  private teardownHls(): void {
    this.hls?.destroy();
    this.hls = null;
  }

  private emit(event: PlayerEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  private readonly onPlaying = (): void => {
    if (!this.firstFrameSeen) {
      this.firstFrameSeen = true;
      this.emit({ type: 'firstFrame', atMs: this.options.elapsedMs() - this.playStartedAtMs });
    }
    this.emit({ type: 'playing' });
    this.startVitals();
  };

  /**
   * Guarda se i fotogrammi arrivano davvero, mentre la riproduzione va avanti.
   *
   * ## Il guasto che questo controllo riconosce
   *
   * Il 04-10-2026, un canale su iPhone: **audio sì, schermo nero**. Nessun errore sull'elemento,
   * nessun errore da hls.js, nessun evento. L'app non sapeva di essere nera — e quel che chi guarda
   * impara, in quel caso, è «non funziona».
   *
   * Il flusso arriva, la linea è autorizzata, il contenitore si apre, l'audio si decodifica: quel
   * che non succede è una cosa sola, il decoder video rifiuta la sua traccia **in silenzio**. Lo
   * dice una misura sola: l'orologio del contenuto avanza e `videoWidth` resta a zero, cioè non è
   * mai stato decodificato un fotogramma.
   *
   * ## Perché un orologio e non un controllo all'avvio
   *
   * Perché all'avvio `videoWidth` è zero su **tutti** i canali, compresi quelli che partiranno fra
   * un istante. La differenza fra «non è ancora arrivato» e «non arriverà» è il tempo, e si misura
   * in millisecondi — non in «numero di controlli», che è un tempo che dipende da quanto è lenta la
   * rete, cioè non è un tempo. La regola sta nel dominio (`videoIsBlank`).
   */
  private startVitals(): void {
    if (this.vitalsTimer !== null) return;
    this.playingSinceMs = this.options.elapsedMs();
    this.vitalsTimer = setInterval(() => this.checkVitals(), VITALS_TICK_MS);
  }

  private stopVitals(): void {
    if (this.vitalsTimer !== null) clearInterval(this.vitalsTimer);
    this.vitalsTimer = null;
    this.playingSinceMs = null;
    this.blankReported = false;
  }

  private checkVitals(): void {
    if (this.playingSinceMs === null) return;

    /*
      Il conto dei fotogrammi dove c'è, e `null` dove non c'è.

      Non zero: `null` vuol dire «questo lettore non lo espone», e trattarlo come zero dichiarerebbe
      rotto ogni canale su quei lettori. È la stessa distinzione che il dominio pretende.
    */
    const quality = (
      this.video as unknown as { getVideoPlaybackQuality?: () => { totalVideoFrames: number } }
    ).getVideoPlaybackQuality;
    const totalVideoFrames = typeof quality === 'function' ? quality.call(this.video).totalVideoFrames : null;

    const blank = videoIsBlank({
      videoWidth: this.video.videoWidth,
      totalVideoFrames,
      playingForMs: this.options.elapsedMs() - this.playingSinceMs,
    });
    if (!blank || this.blankReported) return;

    // Una volta sola: il controllo continua a girare perché la situazione può cambiare — un
    // cambio di risoluzione, un flusso che riparte — ma il racconto non si ripete ogni secondo.
    this.blankReported = true;
    this.emit({ type: 'videoBlank' });

    const remedy = blankVideoRemedy(
      { ...capabilitiesOf(this.video), webCodecs: WEBCODECS_PLAYER_EXISTS },
      this.pipeline,
    );
    if (remedy.action === 'report') {
      this.emit({
        type: 'error',
        // Non `decoder_unsupported`: quello è il decoder che **rifiuta** e lo dice. Qui accetta, e
        // non produce niente. Sono due guasti diversi e vanno in due segnalazioni diverse.
        error: { code: remedy.code, params: {}, retryable: false, atMs: Date.now() },
      });
    }
  }

  /**
   * L'errore dell'elemento `<video>`, che è l'unico che arriva sul percorso del file.
   *
   * Con hls.js attaccato gli errori li racconta hls.js, e questo si tace per non dire la stessa cosa
   * due volte con due nomi diversi. Sul percorso nativo — un film — non c'è nessun altro che parli:
   * senza questa riga un MP4 che non arriva lascia la schermata su «Avvio…» fino al tempo massimo, e
   * poi dichiara un timeout per un file che aveva risposto 403 in mezzo secondo.
   *
   * I quattro codici di `MediaError` si distinguono, perché hanno rimedi diversi: una rete che cade
   * si riprova, un formato che il decoder rifiuta no — è la macchina, non il contenuto.
   */
  private readonly onMediaError = (): void => {
    if (this.hls !== null) return;
    const atMs = Date.now();
    const code = this.video.error?.code;
    if (code === 2 /* MEDIA_ERR_NETWORK */) {
      this.emit({ type: 'error', error: { code: 'stream.not_available', params: {}, retryable: true, atMs } });
      return;
    }
    if (code === 3 /* MEDIA_ERR_DECODE */ || code === 4 /* MEDIA_ERR_SRC_NOT_SUPPORTED */) {
      this.emit({
        type: 'error',
        error: { code: 'stream.decoder_unsupported', params: {}, retryable: false, atMs },
      });
      return;
    }
    // `MEDIA_ERR_ABORTED` è l'utente che se ne va, o noi che cambiamo sorgente: non è un guasto e
    // non si racconta come tale.
  };

  private readonly onPause = (): void => this.emit({ type: 'paused' });
  private readonly onWaiting = (): void => this.emit({ type: 'stalled' });
  private readonly onEnded = (): void => this.emit({ type: 'ended' });
  private readonly onTimeUpdate = (): void =>
    this.emit({
      type: 'timeUpdate',
      positionSec: this.video.currentTime,
      durationSec: Number.isFinite(this.video.duration) ? this.video.duration : 0,
    });

  /**
   * Gli errori si traducono uno per uno.
   *
   * «Un decoder che rifiuta un flusso e una rete assente sono guasti diversi, con rimedi
   * diversi, e vanno detti diversi» (06-RIPRODUZIONE §6). Un `hls.js` che rifiuta tutto
   * con «errore di riproduzione» è esattamente l'app che stiamo cercando di non scrivere.
   */
  private translateHlsError(type: string, details: string, status?: number): GatewayError {
    const atMs = Date.now();

    /*
      Lo stato HTTP prima di tutto, quando c'è.

      Il gateway risponde **subito** e con precisione: 503 «Stream is not currently active», 403
      «content not included in your subscription». Buttare via quel numero e raccontarli entrambi
      come un avvio lento è la cosa che il §6 vieta — «un'app che dice sempre errore di
      riproduzione» — e costa all'utente quindici secondi di attesa per una risposta già arrivata.
    */
    if (status === 503) {
      return { code: 'stream.not_available', params: { status }, retryable: false, atMs };
    }
    if (status === 403) {
      return { code: 'stream.not_in_subscription', params: { status }, retryable: false, atMs };
    }

    if (details === ErrorDetails.MANIFEST_LOAD_ERROR || details === ErrorDetails.MANIFEST_LOAD_TIMEOUT) {
      return { code: 'stream.start_timeout', params: { details }, retryable: true, atMs };
    }
    if (type === ErrorTypes.MEDIA_ERROR) {
      return { code: 'stream.decoder_unsupported', params: { details }, retryable: false, atMs };
    }
    if (type === ErrorTypes.NETWORK_ERROR) {
      return { code: 'stream.source_dropped', params: { details }, retryable: true, atMs };
    }
    return { code: 'stream.source_dropped', params: { details, type }, retryable: true, atMs };
  }
}
